import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { formatDate, formatDateTime } from "../../hooks/dateTimeConvertion";
import {
  getLicenseKindLabel,
  isSessionLicense,
} from "../../hooks/useLicenseType";
import {
  timeRemainingTemplate,
  licenseSeatColumns,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { License, LicenseSeat } from "../../store/types";
import { Container } from "../../components/Container/Container";
import CustomDropdown from "../../components/CustomInput/CustomDropdown";
import CustomInputNumberIncrement from "../../components/CustomInput/CustomInputNumberIncrement";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import type { DatatableHandle } from "../../components/CustomDatatable/CustomDatatable";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import LicenseMembersPickList from "../../components/LicenseMembersPickList/LicenseMembersPickList";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { H2, DangerText, MediumText } from "../../styles/Typography";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import {
  IconUserFilled,
  IconCaseFilled,
  IconCompanyFilled,
  IconDocFilled,
  IconCalendarFilled,
  IconCalendarCheckFilled,
  IconCalendarXFilled,
  IconArrowLeft,
  IconCircleCheckFilled,
  IconUser,
  IconEdit,
  IconDelete,
  IconSearch,
  IconCircleCheck,
  IconCircleX,
  IconEditUser,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  FlexColumn,
  ButtonContainer,
  ButtonLabel,
  EditButtonLabel,
  EditButtonInnerContainer,
  DropdownColumn,
  SmallColumn,
  IsEnabled,
  Flex,
  InfoBlock,
  ButtonBlock,
  OuterHardwareContainer,
  SpaceContainer,
  HardwareInfoContainer,
  HardwareInfoContainerMobile,
  HardwareInfoInnerBox,
  HardwareInfoHeader,
  HardwareTextLine,
  HardwareInfoH2,
  HardwareInfoText,
  HardwareInfoH,
  PageName,
  SessionContainer,
  SessionContainerMobile,
  SessionCards,
  SessionCard,
  SessionNumber,
  OnlineNumber,
  NumberTag,
  OnlineNumberTag,
  ConfirmButtonContainer,
  SessionCardMobile,
  InputNumberContainer,
  InputNumberMobileFlex,
  SearchContainer,
  InputButtonContainer,
  InputContainer,
  Line,
} from "./LicenseDetails.styles";
import {
  FlexLineJustify,
  ButtonBox,
  Label,
  Tag,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const seatName = (seat?: LicenseSeat | null): string =>
  seat?.fullName || seat?.shortName || "Не добавлен";

const LicenseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;

  const tableRef = useRef<DatatableHandle<any>>(null);
  const [selectedSeat, setSelectedSeat] = useState<any | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editMaxMembers, setEditMaxMembers] = useState<boolean>(false);
  const [maxMembers, setMaxMembers] = useState<number | null>(null);
  const [comment, setComment] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [editMembers, setEditMembers] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [reseting, setReseting] = useState<boolean>(false);
  const [fingerprintSeat, setFingerprintSeat] = useState<LicenseSeat | null>(
    null
  );
  const [resetSeatTarget, setResetSeatTarget] = useState<LicenseSeat | null>(
    null
  );
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");

  const {
    licensesEP,
    toastRef,
    handleUpdateLicense,
    handleAssignSeat,
    handleRemoveSeat,
    handleResetSeat,
    handleDeleteLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleEditMaxActiveUsers,
    refetchData,
  } = useLicensesAPI(undefined, { skipList: true });
  const { users } = useUsersAPI();

  // GET /api/v2/licenses/{id} — карточка лицензии вместе с местами (seats)
  const {
    data: license,
    refetch: refetchLicense,
    isLoading,
  } = useGetQuery(`${licensesEP}/${id}`, {
    refetchOnMountOrArgChange: true,
    skip: !id || !licensesEP,
  });

  const seats: LicenseSeat[] = useMemo(
    () => (license as License | undefined)?.seats ?? [],
    [license]
  );
  const isSession = isSessionLicense(license?.kind);
  const onlineCount = useMemo(
    () => seats.filter((seat) => seat.isOnline).length,
    [seats]
  );
  const singleSeat = !isSession && seats.length > 0 ? seats[0] : null;

  const tableData = useMemo(
    () =>
      seats.map((seat) => ({
        ...seat,
        online: seat.isOnline ? "Онлайн" : "Оффлайн",
      })),
    [seats]
  );

  const companyUsersOpt = useMemo(() => {
    if (!license?.organizationId || !users) return [];
    return users
      .filter((user: any) => user.organizationId === license.organizationId)
      .map((user: any) => ({
        label: `${user.lastName ?? ""} ${user.name ?? ""} ${
          user.patronymic ?? ""
        }`,
        value: user.id,
      }));
  }, [license?.organizationId, users]);

  const membersOpt = useMemo(
    () =>
      companyUsersOpt.map((option) => ({
        id: String(option.value),
        fullName: option.label,
      })),
    [companyUsersOpt]
  );

  useEffect(() => {
    if (license) {
      setComment(license.comments || "");
      setEnabled(license.isEnabled);
      setMaxMembers(license.maxActiveUsers ?? null);
    }
  }, [license]);

  useEffect(() => {
    if (seats.length > 0) setSelectedUser(null);
  }, [seats]);

  const handleSave = async () => {
    if (!license) return;
    setSaving(true);
    let success = true;

    try {
      if (enabled !== license.isEnabled) {
        success = enabled
          ? await handleActivateLicense(license.id)
          : await handleDeactivateLicense(license.id);
        if (!success) return;
      }

      if (selectedUser) {
        success = await handleAssignSeat(license.id, selectedUser);
        if (!success) return;
      }

      if (comment !== (license.comments ?? "")) {
        success = await handleUpdateLicense(
          {
            startsAt: license.startsAt,
            expiresAt: license.expiresAt,
            comments: comment,
          },
          license.id
        );
        if (!success) return;
      }
      refetchLicense();
    } finally {
      setSaving(false);
    }
  };

  /** POST /api/v2/licenses/{licenseId}/seats/{userId}/reset */
  const handleResetFingerprint = async () => {
    if (!license || !resetSeatTarget) return;
    setReseting(true);

    try {
      const success = await handleResetSeat(license.id, resetSeatTarget.userId);
      if (success) refetchLicense();
    } finally {
      setReseting(false);
      setResetSeatTarget(null);
    }
  };

  const handleEdit = () => {
    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const handleDelete = () => {
    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicenseAPI = async (): Promise<boolean> => {
    try {
      return await handleDeleteLicense(license);
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  const handleViewUser = (seat: any) => {
    navigate(`/users/${seat.userId}`);
  };

  const handleEditUser = () => {
    if (isMobile) {
      navigate(`/licenses/form?mode=editUser&id=${license.id}`);
    } else {
      setLicenseDialogType("editUser");
      setLicenseDialogVisible(true);
    }
  };

  const handleDeleteUser = () => {
    if (isMobile) {
      navigate(`/licenses/form?mode=deleteUser&id=${license.id}`);
    } else {
      setLicenseDialogType("deleteUser");
      setLicenseDialogVisible(true);
    }
  };

  const removeSeat = async (seat: LicenseSeat) => {
    const success = await handleRemoveSeat(license.id, seat.userId);
    if (success) refetchLicense();
  };

  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return tableData;
    const lowerSearch = searchValue.toLowerCase();
    return tableData.filter(
      (seat: any) =>
        seat.fullName?.toLowerCase().includes(lowerSearch) ||
        seat.online?.toLowerCase().includes(lowerSearch)
    );
  }, [tableData, searchValue]);

  const resetInputNumber = () => {
    setEditMaxMembers(false);
    setMaxMembers(license?.maxActiveUsers ?? 0);
  };

  const resetAllFilters = () => {
    if (tableRef.current) {
      tableRef.current.reset();
    }
    setSearchValue("");
  };

  /** PUT /api/v2/licenses/{id}/max-active-users */
  const handleEditMaxMembers = async () => {
    if (!license || !maxMembers) return;

    try {
      const success = await handleEditMaxActiveUsers(license.id, maxMembers);
      if (success) refetchLicense();
    } finally {
      setEditMaxMembers(false);
    }
  };

  const seatActions = (seat: any) => (
    <ButtonBox>
      <Button
        text
        tooltip="Информация об оборудовании"
        tooltipOptions={{ position: "top" }}
        onClick={() => setFingerprintSeat(seat)}
      >
        <IconDocFilled size={16} />
      </Button>
      <Button
        text
        tooltip="Сбросить оборудование"
        tooltipOptions={{ position: "top" }}
        onClick={() => setResetSeatTarget(seat)}
      >
        <IconCircleX size={16} />
      </Button>
      <Button
        text
        tooltip="Удалить из лицензии"
        tooltipOptions={{ position: "top" }}
        onClick={() => removeSeat(seat)}
      >
        <IconDelete size={16} />
      </Button>
    </ButtonBox>
  );

  const seatColumns = useMemo(
    () => [
      ...licenseSeatColumns,
      { field: "actions", header: "", body: seatActions },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [license]
  );

  const fingerprintBlock = (fingerprint: any) => (
    <HardwareInfoText>
      {fingerprint
        ? Object.entries(fingerprint).map(([key, value]) => (
            <HardwareTextLine key={key}>
              <HardwareInfoH>{key}:</HardwareInfoH>{" "}
              {value != null ? String(value) : "—"}
            </HardwareTextLine>
          ))
        : "Нет данных"}
    </HardwareInfoText>
  );

  const seatsSection = (
    <>
      <FlexLineJustify>
        <PageName>
          <H2>{isSession ? "Сессионные пользователи" : "Пользователи"}</H2>
          <Tag>
            {isMobile ? "" : "Количество:"} {seats.length}
          </Tag>
        </PageName>
      </FlexLineJustify>

      <SessionCards>
        {license?.maxActiveUsers != null &&
          (editMaxMembers ? (
            isMobile ? (
              <SessionCardMobile>
                <MediumText>
                  Максимальное количество активных пользователей:
                </MediumText>
                <InputNumberMobileFlex>
                  <InputNumberContainer>
                    <CustomInputNumberIncrement
                      value={maxMembers}
                      setValue={setMaxMembers}
                    />
                  </InputNumberContainer>
                  <ConfirmButtonContainer>
                    <Button
                      text
                      style={{ padding: "5px" }}
                      onClick={handleEditMaxMembers}
                    >
                      <IconCircleCheck size={20} />
                    </Button>
                    <Button
                      text
                      onClick={resetInputNumber}
                      style={{ padding: "5px" }}
                    >
                      <IconCircleX size={20} />
                    </Button>
                  </ConfirmButtonContainer>
                </InputNumberMobileFlex>
              </SessionCardMobile>
            ) : (
              <SessionCard>
                <MediumText>
                  Максимальное количество активных пользователей:
                </MediumText>
                <InputNumberContainer>
                  <CustomInputNumberIncrement
                    value={maxMembers}
                    setValue={setMaxMembers}
                  />
                </InputNumberContainer>
                <ConfirmButtonContainer>
                  <Button
                    text
                    style={{ padding: "5px" }}
                    onClick={handleEditMaxMembers}
                  >
                    <IconCircleCheck size={20} />
                  </Button>
                  <Button
                    text
                    onClick={resetInputNumber}
                    style={{ padding: "5px" }}
                  >
                    <IconCircleX size={20} />
                  </Button>
                </ConfirmButtonContainer>
              </SessionCard>
            )
          ) : (
            <SessionCard>
              <NumberTag>
                <SessionNumber>{license.maxActiveUsers}</SessionNumber>
              </NumberTag>
              <MediumText>
                Максимальное количество активных пользователей
              </MediumText>
              <div>
                <Button text onClick={() => setEditMaxMembers(true)}>
                  <IconEdit size={16} />
                </Button>
              </div>
            </SessionCard>
          ))}

        <SessionCard>
          <OnlineNumberTag>
            <OnlineNumber>{onlineCount}</OnlineNumber>
          </OnlineNumberTag>
          <MediumText>Активных пользователей сейчас</MediumText>
        </SessionCard>
      </SessionCards>

      {editMembers ? (
        <LicenseMembersPickList
          licenseId={license?.id || ""}
          currentMembers={seats}
          allUsers={membersOpt}
          maxMembers={license?.maxActiveUsers || seats.length}
          handleAssignSeat={handleAssignSeat}
          handleRemoveSeat={handleRemoveSeat}
          onHide={() => setEditMembers(false)}
          refetchData={() => {
            refetchLicense();
          }}
        />
      ) : (
        <>
          <SearchContainer>
            <InputContainer>
              <CustomInputText
                value={searchValue}
                setValue={setSearchValue}
                placeholder="Поиск..."
                icon={<IconSearch size={20} />}
              />
            </InputContainer>
            <InputButtonContainer>
              <Button
                icon="pi pi-filter-slash"
                onClick={resetAllFilters}
                outlined
              >
                <Label>Сбросить</Label>
              </Button>
              {isSession && (
                <Button
                  severity="secondary"
                  onClick={() => setEditMembers(true)}
                >
                  <EditButtonInnerContainer>
                    <IconEditUser size={24} />
                    <EditButtonLabel>Редактировать</EditButtonLabel>
                  </EditButtonInnerContainer>
                </Button>
              )}
            </InputButtonContainer>
          </SearchContainer>
          <CustomDatatable
            ref={tableRef}
            data={filteredData ?? []}
            columns={seatColumns}
            onView={handleViewUser}
            onRowDoubleClick={handleViewUser}
            getRowId={(seat) => seat.userId}
            selected={selectedSeat}
            onSelectionChange={(seat) => setSelectedSeat(seat)}
            hideEditButton
            hideDeleteButton
            sortable
          />
        </>
      )}
    </>
  );

  if (isLoading || !license)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />

      <Dialog
        visible={resetSeatTarget !== null}
        onHide={() => setResetSeatTarget(null)}
        header="Информация об оборудовании"
        footer={
          <div>
            <Button
              label="Отмена"
              onClick={() => setResetSeatTarget(null)}
              outlined
            />
            <Button
              label="Сбросить"
              loading={reseting}
              onClick={handleResetFingerprint}
            />
          </div>
        }
        style={{ width: "30rem" }}
      >
        <DangerText>
          Сбросить информацию об оборудовании для пользователя{" "}
          {seatName(resetSeatTarget)}?
        </DangerText>
      </Dialog>

      <Dialog
        visible={fingerprintSeat !== null}
        onHide={() => setFingerprintSeat(null)}
        header={`Оборудование: ${seatName(fingerprintSeat)}`}
        style={{ width: "35rem" }}
      >
        <HardwareInfoInnerBox>
          {fingerprintBlock(fingerprintSeat?.fingerprint)}
        </HardwareInfoInnerBox>
      </Dialog>

      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные лицензии</H2>
              <ButtonBox>
                <Button text onClick={handleEdit}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={handleDelete}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </FlexLineJustify>

            <FlexColumn>
              <StyledIconLine
                icon={<IconCaseFilled size={20} />}
                text={license?.projectName ?? license?.projectId ?? "Нет данных"}
              />
              <StyledIconLine
                icon={<IconCompanyFilled size={20} />}
                text={`${license?.organizationName ?? "Нет данных"}`}
              />
              <StyledIconLine
                icon={<IconDocFilled size={20} />}
                text={
                  license?.kind
                    ? `${getLicenseKindLabel(license?.kind)} лицензия`
                    : "Нет данных"
                }
              />
              <StyledIconLine
                icon={<IconCalendarFilled size={20} />}
                text={`Дата выдачи: ${
                  license?.issuedAt
                    ? formatDate(license.issuedAt.toString())
                    : "Нет данных"
                }`}
              />
              <StyledIconLine
                icon={<IconCalendarCheckFilled size={20} />}
                text={`Дата начала: ${
                  license?.startsAt
                    ? formatDate(license.startsAt.toString())
                    : "Нет данных"
                }`}
              />
              <StyledIconLine
                icon={<IconCalendarXFilled size={20} />}
                text={`Дата завершения: ${
                  license?.expiresAt
                    ? formatDate(license.expiresAt.toString())
                    : "Бессрочно"
                }`}
              />
              <StyledIconLine
                icon={<IconCircleCheckFilled size={20} />}
                text={
                  <MediumText>
                    Оставшееся время:{" "}
                    {license ? timeRemainingTemplate(license) : "Нет данных"}
                  </MediumText>
                }
              />
              <StyledIconLine
                icon={<IconCalendarFilled size={20} />}
                text={`Последняя активность: ${
                  seats.some((seat) => seat.lastHandshakeAt)
                    ? formatDateTime(
                        seats
                          .map((seat) => seat.lastHandshakeAt)
                          .filter(Boolean)
                          .sort()
                          .reverse()[0] as string
                      )
                    : "Не использовалась"
                }`}
              />
            </FlexColumn>
          </Container>
        </CardsColumnContainer>

        <ButtonContainer>
          <Button outlined onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} />
            <ButtonLabel>Назад</ButtonLabel>
          </Button>
        </ButtonContainer>
      </ContainerColumnFlex>

      <Flex>
        <InfoBlock>
          <H2>Управление лицензией</H2>

          <IsEnabled>
            <InputSwitch
              checked={enabled}
              onChange={(e: InputSwitchChangeEvent) => setEnabled(e.value)}
            />
            {enabled ? (
              <MediumText>Лицензия активирована</MediumText>
            ) : (
              <MediumText>Лицензия деактивирована</MediumText>
            )}
          </IsEnabled>

          {!isSession && singleSeat && (
            <Line>
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={seatName(singleSeat)}
              />
              <ButtonBox>
                <Button text onClick={handleEditUser}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={handleDeleteUser}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </Line>
          )}
          {!isSession && !singleSeat && (
            <>
              {companyUsersOpt.length === 0 ? (
                <DangerText>В этой компании нет пользователей</DangerText>
              ) : (
                <DropdownColumn>
                  <MediumText>Добавить пользователя</MediumText>
                  <CustomDropdown
                    value={selectedUser}
                    onChange={(val) => setSelectedUser(val)}
                    options={companyUsersOpt}
                    placeholder="Пользователь"
                    icon={<IconUser size={20} />}
                    filter
                  />
                </DropdownColumn>
              )}
            </>
          )}

          <SmallColumn>
            <MediumText>Комментарии</MediumText>
            <InputTextarea
              autoResize
              value={comment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setComment(e.target.value)
              }
              rows={5}
              cols={30}
            />
          </SmallColumn>
          <ButtonBlock>
            <Button label="Сохранить" onClick={handleSave} loading={saving} />
          </ButtonBlock>
        </InfoBlock>

        <SessionContainer>{seatsSection}</SessionContainer>

        {!isSession && (
          <OuterHardwareContainer>
            <SpaceContainer />
            <HardwareInfoContainer>
              <HardwareInfoHeader>
                <HardwareInfoH2>Hardware Info</HardwareInfoH2>
                {singleSeat?.fingerprint && (
                  <Button
                    severity="secondary"
                    tooltip="Сбросить информацию"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => setResetSeatTarget(singleSeat)}
                  >
                    <IconDelete size={20} />
                  </Button>
                )}
              </HardwareInfoHeader>

              <HardwareInfoInnerBox>
                {fingerprintBlock(singleSeat?.fingerprint)}
              </HardwareInfoInnerBox>
            </HardwareInfoContainer>
          </OuterHardwareContainer>
        )}
      </Flex>

      <SessionContainerMobile>{seatsSection}</SessionContainerMobile>

      {!isSession && (
        <HardwareInfoContainerMobile>
          <HardwareInfoHeader>
            <HardwareInfoH2>Hardware Info</HardwareInfoH2>
            {singleSeat?.fingerprint && (
              <Button
                severity="secondary"
                tooltip="Сбросить информацию"
                tooltipOptions={{ position: "top" }}
                onClick={() => setResetSeatTarget(singleSeat)}
              >
                <IconDelete size={20} />
              </Button>
            )}
          </HardwareInfoHeader>

          <HardwareInfoInnerBox>
            {fingerprintBlock(singleSeat?.fingerprint)}
          </HardwareInfoInnerBox>
        </HardwareInfoContainerMobile>
      )}

      <LicenseDialogs
        visible={licenseDialogVisible}
        onHide={() => setLicenseDialogVisible(false)}
        dialogType={licenseDialogType}
        license={license}
        usersList={users}
        enrichedLicense={{
          ...license,
          kindLabel: getLicenseKindLabel(license.kind),
        }}
        handleEditLicense={handleUpdateLicense}
        handleAssignSeat={handleAssignSeat}
        handleRemoveSeat={handleRemoveSeat}
        handleDeleteLicense={deleteLicenseAPI}
        refetchData={refetchLicense}
        licensesEP={licensesEP}
      />
    </ContainerFlex>
  );
};

export default LicenseDetails;
